// Composicion de la etiqueta adhesiva del QR para la NIIMBOT B1.
// No depende de React ni de la impresora: recibe el SVG del codigo y la direccion,
// y devuelve un canvas al tamaño real del papel, listo para imprimir o guardar.

// El papel se describe tal y como entra en la impresora: el ancho es lo que cruza
// el cabezal y el largo lo que avanza. Las pegatinas del equipo son de 30x50 mm y
// entran por el lado largo: salen de la impresora con 50 de ancho y avanzan 30.
// Como se leen en vertical, el dibujo se gira solo (ver LECTURA).
export const PAPEL = { anchoMm: 50, largoMm: 30 };
export const PPP = 203;
// Como se lee la etiqueta una vez pegada. "vertical": codigo arriba y direccion debajo;
// "horizontal": codigo a la izquierda y direccion al lado. Si el papel entra girado
// respecto a como se lee, el dibujo se gira solo.
export const LECTURA = "vertical";
// Cabezal de la B1: 384 puntos (48 mm). Se sustituye por el que declare la impresora
export const CABEZAL_PX = 384;

// Blanco alrededor del codigo, en modulos del QR: la norma pide 4 para que se lea bien
const MODULOS_MARGEN = 4;
// Margen de la direccion respecto al borde del papel
const MARGEN_TEXTO_MM = 1.5;
// Letra de la direccion, en puntos de impresora. Por debajo de 16 (unos 2 mm) la
// termica la deja ilegible, asi que si no cabe se recorta en vez de encoger mas
const LETRA_MIN_PX = 16;
const LETRA_MAX_PX = 30;
const ALTO_LINEA = 1.2;
const FUENTE = (px) => `bold ${px}px Arial, Helvetica, sans-serif`;

export const mmAPx = (mm) => Math.round((mm / 25.4) * PPP);

// "CP municipio" para la segunda linea de la direccion. En la BD hay codigos postales
// guardados como numero, sin el cero de delante (3690 en vez de 03690)
export const lineaCodigoPostal = (codigoPostal, municipio) => {
  const cp = String(codigoPostal ?? "").trim();
  return [/^\d{1,4}$/.test(cp) ? cp.padStart(5, "0") : cp, municipio].filter(Boolean).join(" ");
};

// Parte un texto en las lineas que quepan en el ancho dado. Una palabra que por si
// sola no cabe (una referencia catastral, un nombre de calle sin espacios) se corta
// por letras, para que nunca se salga del papel.
export const partirEnLineas = (ctx, texto, anchoMax) => {
  const lineas = [];
  let actual = "";
  const cabe = (t) => ctx.measureText(t).width <= anchoMax;

  for (const palabra of String(texto).split(/\s+/).filter(Boolean)) {
    const prueba = actual ? actual + " " + palabra : palabra;
    if (cabe(prueba)) {
      actual = prueba;
      continue;
    }
    if (actual) lineas.push(actual);
    actual = "";
    if (cabe(palabra)) {
      actual = palabra;
      continue;
    }
    for (const letra of palabra) {
      if (actual && !cabe(actual + letra)) {
        lineas.push(actual);
        actual = "";
      }
      actual += letra;
    }
  }
  if (actual) lineas.push(actual);
  return lineas;
};

// Recorta una linea hasta que quepa con "…" al final
const conPuntosSuspensivos = (ctx, linea, anchoMax) => {
  let recorte = linea.trimEnd();
  while (recorte && ctx.measureText(recorte + "…").width > anchoMax) recorte = recorte.slice(0, -1).trimEnd();
  return recorte + "…";
};

// Busca la letra mas grande con la que la direccion entra entera en el hueco. Si ni con
// la minima entra, se queda en la minima y se recorta la direccion con "…", pero el
// codigo postal y el municipio se conservan siempre, que es lo que identifica la finca.
export const ajustarDireccion = (ctx, partes, ancho, alto) => {
  for (let tamano = LETRA_MAX_PX; tamano >= LETRA_MIN_PX; tamano--) {
    ctx.font = FUENTE(tamano);
    const lineas = partes.flatMap((parte) => partirEnLineas(ctx, parte, ancho));
    if (lineas.length * tamano * ALTO_LINEA <= alto) return { tamano, lineas, recortada: false };
  }

  const tamano = LETRA_MIN_PX;
  ctx.font = FUENTE(tamano);
  const maxLineas = Math.max(1, Math.floor(alto / (tamano * ALTO_LINEA)));
  const final = partes.length > 1 ? partirEnLineas(ctx, partes[partes.length - 1], ancho) : [];
  const delante = partes.slice(0, final.length ? -1 : undefined).flatMap((parte) => partirEnLineas(ctx, parte, ancho));
  const huecoDelante = Math.max(1, maxLineas - final.length);

  let lineas = delante.slice(0, huecoDelante);
  if (delante.length > huecoDelante) {
    lineas[lineas.length - 1] = conPuntosSuspensivos(ctx, lineas[lineas.length - 1], ancho);
  }
  lineas = [...lineas, ...final].slice(0, maxLineas);
  return { tamano, lineas, recortada: true };
};

// Numero de modulos del QR: react-qr-code pone un viewBox de un modulo por unidad
const modulosDelSvg = (svg) => {
  const caja = (svg.getAttribute("viewBox") || "").split(/\s+/).map(Number);
  return caja[2] > 0 ? caja[2] : null;
};

const cargarImagen = (src) =>
  new Promise((listo, fallo) => {
    const imagen = new Image();
    imagen.onload = () => listo(imagen);
    imagen.onerror = fallo;
    imagen.src = src;
  });

/**
 * Dibuja la etiqueta al tamaño real del papel.
 * @param {SVGElement} svg      El QR tal como lo pinta react-qr-code
 * @param {string[]}   partes   Lineas de la direccion: [calle, "CP municipio"]
 * @param {number}     cabezalPx Puntos del cabezal de la impresora conectada
 */
export const componerEtiqueta = async ({ svg, partes = [], cabezalPx = CABEZAL_PX }) => {
  if (!svg) throw new Error("No hay QR que imprimir");

  // El ancho del papel nunca puede superar el cabezal: se recortaria por un lado
  const anchoPapel = Math.min(mmAPx(PAPEL.anchoMm), cabezalPx);
  const largoPapel = mmAPx(PAPEL.largoMm);

  // Medidas tal y como se lee la etiqueta pegada
  const lecturaVertical = LECTURA === "vertical";
  const ladoCorto = Math.min(anchoPapel, largoPapel);
  const ladoLargo = Math.max(anchoPapel, largoPapel);
  const ancho = lecturaVertical ? ladoCorto : ladoLargo;
  const alto = lecturaVertical ? ladoLargo : ladoCorto;
  const girar = ancho !== anchoPapel;

  const lienzo = document.createElement("canvas");
  lienzo.width = anchoPapel;
  lienzo.height = largoPapel;
  const ctx = lienzo.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, anchoPapel, largoPapel);
  if (girar) {
    // A partir de aqui se dibuja como si la etiqueta estuviese en su posicion de lectura
    ctx.translate(anchoPapel, 0);
    ctx.rotate(Math.PI / 2);
  }

  // Cada modulo del QR ocupa un numero entero de puntos. Escalado a ojo, en 30 mm cada
  // modulo caia en 3,4 puntos y la termica los sacaba de tamaños desiguales
  const modulos = modulosDelSvg(svg) || 33;
  const huecoQr = lecturaVertical ? ancho : Math.min(alto, ancho * 0.6);
  const puntosModulo = Math.max(1, Math.floor(huecoQr / (modulos + MODULOS_MARGEN * 2)));
  const ladoQr = modulos * puntosModulo;
  const margenQr = MODULOS_MARGEN * puntosModulo;
  const margenTexto = mmAPx(MARGEN_TEXTO_MM);

  const qrX = lecturaVertical ? Math.round((ancho - ladoQr) / 2) : margenQr;
  const qrY = lecturaVertical ? margenQr : Math.round((alto - ladoQr) / 2);

  const copia = svg.cloneNode(true);
  copia.setAttribute("width", ladoQr);
  copia.setAttribute("height", ladoQr);
  copia.setAttribute("shape-rendering", "crispEdges");
  const imagenQr = await cargarImagen(
    "data:image/svg+xml;charset=utf-8," + encodeURIComponent(new XMLSerializer().serializeToString(copia))
  );
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(imagenQr, qrX, qrY, ladoQr, ladoQr);

  // Hueco de la direccion: debajo del codigo, o a su derecha, respetando su margen
  const xTexto = lecturaVertical ? margenTexto : qrX + ladoQr + margenQr;
  const yTexto = lecturaVertical ? qrY + ladoQr + margenQr : margenTexto;
  const anchoTexto = lecturaVertical ? ancho - margenTexto * 2 : ancho - xTexto - margenTexto;
  const altoTexto = lecturaVertical ? alto - yTexto - margenTexto : alto - margenTexto * 2;

  const textos = partes.map((p) => String(p ?? "").trim()).filter(Boolean);
  let recortada = false;
  if (textos.length && anchoTexto > 0 && altoTexto > 0) {
    const ajuste = ajustarDireccion(ctx, textos, anchoTexto, altoTexto);
    recortada = ajuste.recortada;
    const altoLinea = ajuste.tamano * ALTO_LINEA;
    ctx.fillStyle = "#000000";
    ctx.textBaseline = "top";
    ctx.textAlign = "center";
    ctx.font = FUENTE(ajuste.tamano);
    // El bloque de texto queda centrado en su hueco
    let y = yTexto + Math.max(0, (altoTexto - ajuste.lineas.length * altoLinea) / 2);
    for (const linea of ajuste.lineas) {
      ctx.fillText(linea, xTexto + anchoTexto / 2, y);
      y += altoLinea;
    }
  }

  return { lienzo, recortada, puntosModulo };
};

// La B1 guia el papel por el centro del cabezal. Si el rollo es mas estrecho que el
// cabezal (30 mm frente a 48), se le manda el ancho completo con la etiqueta centrada:
// asi sale en su sitio tanto si la impresora centra la imagen como si la alinea a un lado
export const centrarEnCabezal = (lienzo, cabezalPx = CABEZAL_PX) => {
  if (lienzo.width >= cabezalPx) return lienzo;
  const completo = document.createElement("canvas");
  completo.width = cabezalPx;
  completo.height = lienzo.height;
  const ctx = completo.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, completo.width, completo.height);
  ctx.drawImage(lienzo, Math.floor((cabezalPx - lienzo.width) / 2), 0);
  return completo;
};
